import { useDeferredValue, useMemo, useState } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import type { CompanyJobSummary } from '@tabbio/types';
import { useCompanyJobs } from '../../hooks/useCompanyJobs';
import {
  availableJobLifecycleActions,
  useJobLifecycleActions,
  type JobLifecycleAction,
} from '../../hooks/useJobLifecycleActions';
import { useLocale } from '../../hooks/useLocale';
import { WebPageHeader } from '../common/WebPageHeader';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import {
  formatCompanyJobLifecycle,
  getCompanyJobMetaLine,
  getCompanyJobsStats,
} from '../../lib/company-jobs';
import { formatRelativeDate } from '../../lib/jobs-format';

type JobTab = 'all' | 'active' | 'draft' | 'closed';

const JOB_TABS: JobTab[] = ['all', 'active', 'draft', 'closed'];

function LoadingRows() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Array.from({ length: 3 }).map((_, index) => (
        <div
          key={index}
          className="tabbio-list-row"
          style={{ pointerEvents: 'none', alignItems: 'flex-start', minHeight: 84 }}
        >
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div
              style={{
                width: '48%',
                height: 16,
                borderRadius: 8,
                background: 'rgba(0,0,0,0.06)',
              }}
            />
            <div
              style={{
                width: '72%',
                height: 13,
                borderRadius: 6,
                background: 'rgba(0,0,0,0.06)',
              }}
            />
            <div
              style={{
                width: '40%',
                height: 22,
                borderRadius: 9999,
                background: 'rgba(0,0,0,0.06)',
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function CenterState({
  title,
  subtitle,
  actionLabel,
  onAction,
  detail,
  icon,
}: {
  title: string;
  subtitle: string;
  actionLabel?: string;
  onAction?: () => void;
  detail?: string | null;
  icon?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        padding: '56px 16px',
        textAlign: 'center',
      }}
    >
      {icon ? (
        <span
          aria-hidden
          style={{ color: 'var(--text-tertiary)', marginBottom: 4 }}
        >
          <MaterialSymbol name={icon} size={40} />
        </span>
      ) : null}
      <div
        style={{
          fontSize: 18,
          fontWeight: 500,
          color: 'var(--text-primary)',
        }}
      >
        {title}
      </div>
      <div
        style={{
          maxWidth: 420,
          fontSize: 14,
          lineHeight: 1.5,
          color: 'var(--text-secondary)',
        }}
      >
        {subtitle}
      </div>
      {detail ? (
        <div
          style={{
            maxWidth: 480,
            fontSize: 12,
            lineHeight: 1.5,
            color: 'var(--text-tertiary)',
          }}
        >
          {detail}
        </div>
      ) : null}
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          style={{
            marginTop: 8,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 40,
            padding: '0 18px',
            borderRadius: 9999,
            border: 0,
            background: 'var(--text-primary)',
            color: 'var(--bg)',
            fontSize: 14,
            fontWeight: 500,
            fontFamily: 'inherit',
            cursor: 'pointer',
            transition: 'opacity 150ms',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.88';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

const LIFECYCLE_ACTION_LABEL_KEY: Record<JobLifecycleAction, string> = {
  publish: 'jobFlow.lifecycle.publish',
  offline: 'jobFlow.lifecycle.takeOffline',
  reopen: 'jobFlow.lifecycle.reopen',
  archive: 'jobFlow.lifecycle.archive',
  delete: 'jobFlow.lifecycle.delete',
};

function JobRow({
  job,
  onClick,
  onEdit,
  onAction,
  isPending,
}: {
  job: CompanyJobSummary;
  onClick: () => void;
  onEdit: () => void;
  onAction: (action: JobLifecycleAction) => void;
  isPending: boolean;
}) {
  const { t, i18n } = useTranslation('common');
  const { t: tEmployer } = useTranslation('employer');
  const { isRTL } = useLocale();
  const actions = availableJobLifecycleActions(job.status).filter(
    (action) => action !== 'archive',
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
      className="tabbio-list-row"
      style={{
        alignItems: 'flex-start',
        minHeight: 96,
        padding: '18px 22px',
        cursor: 'pointer',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            minWidth: 0,
          }}
        >
          <div
            style={{
              minWidth: 0,
              flex: 1,
              fontSize: 16,
              fontWeight: 500,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {job.title}
          </div>
          <span className="tabbio-menu-badge" style={{ flexShrink: 0 }}>
            {formatCompanyJobLifecycle(t, job.status)}
          </span>
        </div>

        <div
          style={{
            marginTop: 4,
            fontSize: 13,
            color: 'var(--text-secondary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {getCompanyJobMetaLine(t, job)}
        </div>

        <div
          style={{
            marginTop: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <span className="tabbio-menu-badge">
            <MaterialSymbol
              name="group"
              size={14}
              style={{ marginInlineEnd: 4, verticalAlign: 'middle' }}
            />
            {job.applicationCount > 0
              ? t('jobPost.entry.applications', { count: job.applicationCount })
              : t('jobPost.entry.openRolePill')}
          </span>

          <span
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--text-tertiary)',
              whiteSpace: 'nowrap',
            }}
          >
            {formatRelativeDate(job.postedAt ?? job.createdAt, i18n.language)}
          </span>
        </div>

        <div
          style={{
            marginTop: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            disabled={isPending}
            onClick={(event) => {
              event.stopPropagation();
              onEdit();
            }}
            className="tabbio-chip"
            style={{ opacity: isPending ? 0.6 : 1 }}
          >
            <MaterialSymbol name="edit" size={14} />
            {tEmployer('jobFlow.lifecycle.edit')}
          </button>
          {actions.map((action) => (
            <button
              key={action}
              type="button"
              disabled={isPending}
              onClick={(event) => {
                event.stopPropagation();
                onAction(action);
              }}
              className="tabbio-chip"
              style={{
                opacity: isPending ? 0.6 : 1,
                color:
                  action === 'offline' ? 'var(--danger, #d93025)' : undefined,
              }}
            >
              {tEmployer(LIFECYCLE_ACTION_LABEL_KEY[action] as never) as string}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function EmployerJobsScreen() {
  const { t } = useTranslation('common');
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<JobTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const deferredQuery = useDeferredValue(searchQuery.trim().toLowerCase());
  const { jobs, isLoading, isFetching, error, refetch } = useCompanyJobs();
  const lifecycleActions = useJobLifecycleActions();
  const stats = getCompanyJobsStats(jobs);

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      if (activeTab !== 'all' && job.status !== activeTab) return false;
      if (!deferredQuery) return true;
      return [
        job.title,
        job.description,
        job.location,
        job.department,
        job.company.name,
      ]
        .filter((value): value is string => Boolean(value))
        .join(' ')
        .toLowerCase()
        .includes(deferredQuery);
    });
  }, [activeTab, deferredQuery, jobs]);

  const hasSearch = Boolean(searchQuery.trim());
  const hasAuthError = /unauthorized/i.test(error?.message ?? '');

  const handleBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
      return;
    }
    router.push('/(employer)/chat' as never);
  };

  return (
    <div
      className="h-full overflow-y-auto"
      style={{
        backgroundColor: 'var(--bg)',
        minHeight: '80vh',
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
    >
      <div className="tabbio-svc-page">
        <WebPageHeader
          title={t('jobPost.entry.title')}
          subtitle={t('jobPost.entry.subtitle')}
          onBack={handleBack}
          hideBackOnMobile
        >
          <button
            type="button"
            onClick={() =>
              router.push('/(employer)/jobs/create?origin=jobs')
            }
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              height: 40,
              padding: '0 18px',
              borderRadius: 9999,
              border: 0,
              background: 'var(--text-primary)',
              color: 'var(--bg)',
              fontSize: 14,
              fontWeight: 500,
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'opacity 150ms',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.88';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            <MaterialSymbol name="add" size={18} />
            {t('jobPost.actions.postRole')}
          </button>
        </WebPageHeader>

        {/* Search */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            height: 48,
            borderRadius: 9999,
            paddingInline: 18,
            background: 'rgba(0,0,0,0.04)',
            marginBottom: 12,
          }}
        >
          <MaterialSymbol
            name="search"
            size={20}
            style={{ color: 'var(--text-secondary)', flexShrink: 0 }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t('jobPost.entry.searchPlaceholder')}
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              color: 'var(--text-primary)',
              fontSize: 15,
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          {hasSearch ? (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              aria-label="Clear"
              style={{
                width: 28,
                height: 28,
                border: 0,
                borderRadius: '50%',
                background: 'transparent',
                color: 'var(--text-tertiary)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MaterialSymbol name="close" size={18} />
            </button>
          ) : null}
        </div>

        {/* Tabs: gem-chip row matching find-jobs pattern */}
        <div
          role="tablist"
          style={{ display: 'flex', gap: 8, marginBottom: 16 }}
        >
          {JOB_TABS.map((tab) => {
            const selected = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setActiveTab(tab)}
                className={selected ? 'tabbio-chip active' : 'tabbio-chip'}
                style={{ flex: 1, justifyContent: 'center' }}
              >
                {t(`jobPost.entry.tabs.${tab}` as never)}
              </button>
            );
          })}
        </div>

        {isLoading || isFetching ? (
          <LoadingRows />
        ) : error ? (
          <CenterState
            icon="error_outline"
            title={t('jobPost.entry.loadFailed')}
            subtitle={
              hasAuthError
                ? t('jobPost.entry.loadFailedHint', {
                    defaultValue:
                      'Your company session may have expired. Refresh the page or sign in again.',
                  })
                : t('jobPost.entry.loadFailedHint')
            }
            detail={error.message}
            actionLabel={t('common:retry')}
            onAction={() => refetch()}
          />
        ) : filteredJobs.length === 0 ? (
          <CenterState
            icon={hasSearch ? 'search_off' : 'work'}
            title={
              hasSearch
                ? t('jobPost.entry.emptyFilteredTitle')
                : t('jobPost.entry.emptyTitle')
            }
            subtitle={
              hasSearch
                ? t('jobPost.entry.emptyFilteredSubtitle')
                : t('jobPost.entry.emptySubtitle')
            }
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filteredJobs.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                onClick={() =>
                  router.push(`/(employer)/jobs/${job.id}` as never)
                }
                onEdit={() =>
                  router.push(`/(employer)/jobs/${job.id}/edit` as never)
                }
                onAction={(action) => void lifecycleActions.run(action, job.id)}
                isPending={lifecycleActions.isPending(job.id)}
              />
            ))}
          </div>
        )}

        {!isLoading && !error && filteredJobs.length > 0 ? (
          <div
            style={{
              marginTop: 20,
              padding: '0 8px',
              fontSize: 12,
              lineHeight: 1.5,
              color: 'var(--text-tertiary)',
            }}
          >
            {t('jobPost.entry.heroOverview', {
              roles: stats.rolesCount,
              applicants: stats.totalApplications,
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
